import React from "react";

import NoData from "~/components/containers/NoData";
import Button from "~/components/mantine/button/Button";
import PendingStatus from "~/components/ui/pendingStatus/PendingStatus";
import { useTeam } from "~/contexts/TeamProvider";
import { usePageTitle } from "~/hooks/usePageTitle";

import { isAutoptoCardRequired } from "../isAutoptoTrial";
import styles from "./CardRequiredGate.module.scss";
import { useCardDialog } from "./useCardDialog";

type Props = {
  // The section's heading, from PAGE_TITLES.
  title: string;
  children: React.ReactNode;
};

const CardRequiredNotice = ({ title }: { title: string }) => {
  usePageTitle(title);
  const { canAddCard, openCardDialog, isOpeningCardDialog, isAwaitingCard } =
    useCardDialog();

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{title}</h1>
      <NoData
        title="Add a card to launch your platform"
        description={
          canAddCard
            ? `${title} open as soon as you add a card. You pay $0 today, and your trial continues.`
            : `${title} open once your team admin adds a card. Ask your team admin to add a card.`
        }
      >
        {canAddCard &&
          (isAwaitingCard ? (
            <PendingStatus>Activating your platform…</PendingStatus>
          ) : (
            <Button onClick={openCardDialog} loading={isOpeningCardDialog}>
              Launch My Platform
            </Button>
          ))}
      </NoData>
    </div>
  );
};

/**
 * A section the backend closes during an Autopto trial until a card is
 * added (Team members, API keys, Services). The menu hides them; a direct
 * link gets a way to the card dialog instead of an empty page or a 403, and
 * the section opens once the card has landed.
 */
const CardRequiredGate = ({ title, children }: Props) => {
  const { team } = useTeam();

  if (!isAutoptoCardRequired(team)) return <>{children}</>;

  return <CardRequiredNotice title={title} />;
};

export default CardRequiredGate;
